import { BehaviorSubject, Subject, combineLatest, merge, identity } from 'rxjs';
import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, Injector, Directive, Inject, Input, Output, Injectable, Optional, NgModule, ViewChildren, Component, SkipSelf } from '@angular/core';
import { AclService, ACL_TAG_TOKEN, wrapAclEvaluationWithTag, AclModule } from '@allianz/taly-acl/angular';
import { scan, map, distinctUntilChanged, startWith, switchMap, defaultIfEmpty, pairwise, tap, takeUntil, take } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { __decorate, __metadata } from 'tslib';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import dayjs from 'dayjs';
import { Validators, FormGroup, FormControl } from '@angular/forms';

/**
 * Cherry picked from Angular Sources
 * to have a dasherize function at hands.
 */
const STRING_DASHERIZE_REGEXP = /[ _]/g;
const STRING_DECAMELIZE_REGEXP = /([a-z\d])([A-Z])/g;
const STRING_CAMELIZE_REGEXP = /(-|_|\.|\s)+(.)?/g;
function camelize(str) {
    return str
        .replace(STRING_CAMELIZE_REGEXP, (_match, _separator, chr) => {
        return chr ? chr.toUpperCase() : '';
    })
        .replace(/^([A-Z])/, (match) => match.toLowerCase());
}
function decamelize(str) {
    return str.replace(STRING_DECAMELIZE_REGEXP, '$1_$2').toLowerCase();
}
function dasherize(str) {
    return decamelize(str).replace(STRING_DASHERIZE_REGEXP, '-');
}

const BUILDING_BLOCK_META_DATA = new InjectionToken('Building Block Meta Data');
const PAGES_CONFIGURATION = new InjectionToken('Pages Configuration');
const BFF_BASE_URL_TOKEN = new InjectionToken('BFF_BASE_URL_TOKEN');
const PLUGIN_VALIDATORS = new InjectionToken('PLUGIN_VALIDATORS', {
    factory: () => []
});
const CHANNEL_TOKEN = new InjectionToken('Channel (retail/expert)', {
    factory: () => "retail" /* RETAIL */
});
const EXPERIMENTAL_NEXT_BUTTON_ALWAYS_ENABLED = new InjectionToken('EXPERIMENTAL_NEXT_BUTTON_ALWAYS_ENABLED');

/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * STATE: Highly fluctuating, expect many refactoring and breaking changes.
 *
 * Our Building Blocks Foundation: The abstract Building Block definition.
 * We need to prepare and implement everything to allow Building Block to deliver the following features:
 *
 * 1. User State
 * 2. Resources (deliver required data like Dropdown lists, things to display only)
 * 3. Support Completion (Facade/PFE Integration)
 * 4. Support Various Lifecycle Methods: onPageConnection (via Facade)
 *
 * Good things that might help us:
 * A) Split different responsibilities into mixins
 * B) Provide a Building Block Decorator, maybe that's easier than extending or beneficial in addition (to be checked)
 * C) Keep Lifecycle out of the interface, it's internal knowledge in the end.
 * D) Proper Code Inspection, extended use of Typescript to ensure:
 *     State is matching, Feature Flags are matching etc.
 */
class AbstractBuildingBlock {
    constructor(injector) {
        this.businesssEventCall$ = new EventEmitter();
        this.navigateEvent$ = new EventEmitter();
        /**
         * can be changed during runtime and given as a default
         * by the implemented Building Block
         */
        this.id = '';
        /**
         * Mark this Building Block as complete.
         * That way a workflow engine can determine the overall completion state of any given pages.
         * This method can be actively triggered by the user (say a button is clicked) or indirectly by
         * a form being changed.
         *
         * The author of a Building Block needs to make sure that this function is called during the lifetime of a Building Block.
         * If the Building Block is purely presentational this method can be called during initialization.
         *
         */
        // That's useful to hook into the state while a Building Block is mounted in a template.
        this.completed = new EventEmitter();
        // have a separate Behaviour Subject to always reflect the current state of completion.
        this.completion$ = new BehaviorSubject(false);
        /**
         * Lifecycle Event called once the Building Block is connected to the page.
         * This can only happen when the Building Block is embed in a Building Block Page
         * This is not intended to be used standalone.
         *
         * At this point you can be sure that:
         * 1. The Building Block is ready (AfterViewInit)
         * 2. The initial state and resources has been set
         */
        this.connected$ = new Subject();
        this.disconnected$ = new Subject();
        /**
         * STATE HANDLING
         */
        /**
         * Call this function to grab the current state and forward it
         * any time your internal state is ready to be forwarded.
         * It's a BehaviorSubject to always deliver the latest state on subscription.
         * TODO: Evaluate if a multicast could help maintaining an more coordiated data stream
         */
        this.stateChange$ = new BehaviorSubject({});
        this._loadingStatus$ = new Subject();
        this.loadingStatus$ = this._loadingStatus$
            .asObservable()
            .pipe(scan(accumulateBusinessEvents, []), map(groupEventStatusesByName), distinctUntilChanged(isEqual));
        this._aclService = injector.get(AclService);
        this._aclTag = injector.get(ACL_TAG_TOKEN, null);
        this.channel = injector.get(CHANNEL_TOKEN);
        this.isRetailChannel = this.channel === "retail" /* RETAIL */;
        this.isExpertChannel = this.channel === "expert" /* EXPERT */;
    }
    /**
     * This method creates a Deferred object for the business event given. This will have a newly created promise and their
     * executor parameters resolve & reject. This deferred object is then emitted along with the business event name
     *
     * The Store Integration(Facade) can then decide when to resolve or reject the underlying promise based on the service
     * activator call attached to the business event.
     *
     * @param event name of the business event
     * @returns the promise from the deferred. The building block can then be notified on the respective service activator
     * call's status.
     */
    callBusinessEvent(event) {
        const deferred = createDeferred();
        this._loadingStatus$.next({ event, loading: true });
        this.businesssEventCall$.emit({ event, deferred });
        return deferred.promise.finally(() => this._loadingStatus$.next({ event, loading: false }));
    }
    navigate(type, payload) {
        const event = {
            type
        };
        if (payload) {
            event.payload = payload;
        }
        this.navigateEvent$.emit(event);
    }
    ngOnInit() {
        if (!this.id) {
            this.id = dasherize(this.constructor.name);
        }
    }
    commitCompletion() {
        this.completion$.next(true);
        this.completed.emit(true);
    }
    revertCompletion() {
        this.completion$.next(false);
        this.completed.emit(false);
    }
    onPageConnection() { }
    onPageDisconnected() { }
    setValidationConfiguration(data) { }
    /**
     * Override if you need to pre process the incoming resource data
     * and if you want to establish some default values. The return value will arrive in setResources
     * and stored in the 'resources' variable. Needs to match the Resource Generic of the given Building Block
     */
    transformResources(data) {
        return data;
    }
    /**
     * The method will be called by the facade
     * and is marked as an @Input. Derived Building Blocks need to list
     * the input in the @Component decorator under `inputs:[]` to make it available to the template (View Engine limitation).
     */
    set resources(data) {
        this._resources = this.transformResources(data);
        this.setResources(this._resources);
    }
    get resources() {
        return this._resources;
    }
    setResources(data) { }
    stateChanged() {
        this.stateChange$.next(this.getState());
    }
    /**
     * Override if you need to pre process the incoming state data
     * and if you want to establish some default values. The return value will arrive in setState
     * and stored in the 'state' variable. Needs to match the State Generic of the given Building Block
     */
    transformState(data) {
        return data;
    }
    /**
     * The method will be called by the facade
     * and is marked as an @Input. Derived Building Blocks need to list
     * the input in the @Component decorator under `inputs:[]` to make it available to the template (View Engine limitation).
     */
    set state(data) {
        this._state = this.transformState(data);
        this.setState(this._state);
    }
    get state() {
        return this._state;
    }
    setState(state) { }
    /**
     * Retrieve the current state of this Building Block. It's an empty object by default.
     * The state should only include data that is useful to the outside world. Don't expose internal states
     * like scroll position, button press states etc. Think of it as data that is useful in an overall business
     * process and that you would also recognize in written insurance contracts for example.
     *
     * The author of a Building Block needs to implement this function
     * in order to deliver a proper snapshot of the current state of all components and/or forms.
     */
    getState() {
        // TODO: That needs to throw an error if T is defined but getState is not overwritten.
        return {};
    }
    get acl() {
        /**
         * we need to expose our AclService bound to the given aclTag
         * of this instance if any. This ensures that calls to
         * this.acl.canShow will incorporate the given acl tag hierarchy.
         * This is important for anything you do manually in the template
         * or for supporting scripts like `autoFormBindingFactory`
         */
        if (this._aclTag) {
            return wrapAclEvaluationWithTag(this._aclService, this._aclTag);
        }
        return this._aclService;
    }
    /**
     * This is supposed to return the building block's form.
     * Leave this untouched if your building block implementation does not have a form.
     */
    getForm() {
        return undefined;
    }
}
AbstractBuildingBlock.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AbstractBuildingBlock, deps: [{ token: Injector }], target: i0.ɵɵFactoryTarget.Directive });
AbstractBuildingBlock.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.9", type: AbstractBuildingBlock, inputs: { id: "id", resources: "resources", state: "state" }, outputs: { completed: "completed" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AbstractBuildingBlock, decorators: [{
            type: Directive
        }], ctorParameters: function () {
        return [{ type: i0.Injector, decorators: [{
                        type: Inject,
                        args: [Injector]
                    }] }];
    }, propDecorators: { id: [{
                type: Input
            }], completed: [{
                type: Output
            }], resources: [{
                type: Input
            }], state: [{
                type: Input
            }] } });
function accumulateBusinessEvents(arr = [], event) {
    return [...arr, event];
}
function groupEventStatusesByName(arr = []) {
    const grouped = arr.reduce((status, { event, loading }) => {
        var _a;
        (_a = status[event]) !== null && _a !== void 0 ? _a : (status[event] = 0);
        status[event] += loading ? 1 : -1;
        return status;
    }, {});
    return Object.entries(grouped).reduce((status, [event, count]) => {
        status[event] = count > 0;
        return status;
    }, {});
}
function createDeferred() {
    const deferred = {};
    deferred.promise = new Promise((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
    });
    return deferred;
}

/**
 * centralize the provider declaration for every Building Block
 * so we provide additional injections from the core without upgrading
 * every single Building Block
 */
function createBuildingBlockProvider(component) {
    return [
        {
            provide: AbstractBuildingBlock,
            useExisting: component
        }
    ];
}

/**
 * Service to access any given meta data for a Building Block page
 * injecteed by BUILDING_BLOCK_META_DATA during the generation of a page.
 * The service is used by Building Block Pages and needs access to the page-local
 * BUILDING_BLOCK_META_DATA, hence we chose the any strategy to always create a local service instance.
 * Working alternative would be injecting it directly into each of the page modules but this lazy approach
 * supports tree shaking way better.
 */
class BuildingBlockMetaService {
    constructor(metaData) {
        this._metaData = {};
        this._metaData = metaData;
    }
    get metaData() {
        return this._metaData;
    }
    getData(buildingBlockId, key) {
        var _a;
        this.assertBuildingBlockExistence(buildingBlockId);
        return (_a = this.metaData[buildingBlockId]) === null || _a === void 0 ? void 0 : _a[key];
    }
    hasData(buildingBlockId, key) {
        return this.hasMetaData(buildingBlockId) && !!this.metaData[buildingBlockId][key];
    }
    getResources(buildingBlockId) {
        return this.getData(buildingBlockId, 'resources');
    }
    getBusinessEvents(buildingBlockId) {
        return this.getData(buildingBlockId, 'businessEvents');
    }
    getNavigationData(buildingBlockId) {
        return this.getData(buildingBlockId, 'navigation');
    }
    hasResources(id) {
        return this.hasMetaData(id) && !!this.metaData[id].resources;
    }
    hasMetaData(id) {
        return Object.keys(this.metaData).includes(id);
    }
    assertBuildingBlockExistence(id) {
        if (!this.hasMetaData(id)) {
            throw new Error(`Could not find key ${id} in metadata list`);
        }
    }
}
BuildingBlockMetaService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockMetaService, deps: [{ token: BUILDING_BLOCK_META_DATA, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
BuildingBlockMetaService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockMetaService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockMetaService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any'
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [BUILDING_BLOCK_META_DATA]
                    }] }];
    } });

let __pluginValidators;
function __setPluginValidators(pluginValidators) {
    if (__pluginValidators === undefined) {
        __pluginValidators = pluginValidators;
    }
}
function isAsyncValidator(validator) {
    return validator.validateAsync !== undefined;
}
function extractPluginValidationFactory(pv) {
    return (item) => ({
        validator: isAsyncValidator(pv)
            ? pv.validateAsync(item.validationParam)
            : pv.validate(item.validationParam),
        validatorName: pv.type,
        errorMessage: item.errorMessage || pv.defaultErrorMessage || '',
        async: isAsyncValidator(pv),
        validationParam: item.validationParam
    });
}
function getPluginValidations() {
    return (__pluginValidators !== null && __pluginValidators !== void 0 ? __pluginValidators : []).reduce((acc, pv) => {
        const pluginValidatorValidation = {
            // until TS supports regex in types, we cannot enforce that "type" field will be uppercase
            [pv.type.toUpperCase()]: extractPluginValidationFactory(pv)
        };
        return Object.assign(Object.assign({}, acc), pluginValidatorValidation);
    }, {});
}

class TalyCoreModule {
    constructor(pluginValidators) {
        this.pluginValidators = pluginValidators;
        __setPluginValidators(this.pluginValidators);
    }
}
TalyCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyCoreModule, deps: [{ token: PLUGIN_VALIDATORS }], target: i0.ɵɵFactoryTarget.NgModule });
TalyCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyCoreModule, exports: [AclModule] });
TalyCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyCoreModule, providers: [BuildingBlockMetaService], imports: [AclModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [BuildingBlockMetaService],
                    exports: [AclModule]
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [PLUGIN_VALIDATORS]
                    }] }];
    } });

class AbstractBuildingBlockFacade {
    constructor(buildingBlock) {
        this.buildingBlock = buildingBlock;
        this._isConnected = false;
        this.disconnected$ = new Subject();
        this.connected$ = new Subject();
        if (!this.buildingBlock) {
            throw new Error('You tried to create a Facade without a Building Block');
        }
        if (!this.id) {
            throw new Error(`Could not read derive from given Building Block. Received ${this.id}`);
        }
    }
    get id() {
        var _a;
        return (_a = this.buildingBlock) === null || _a === void 0 ? void 0 : _a.id;
    }
    static create(block) {
        return new AbstractBuildingBlockFacade(block);
    }
    /**
     * TODO: Implement proper lifecycle
     * Building Blocks will connect when being discovered on a page
     * this means we can define a lifecycle here.
     * Before Connection: Feature Flags, Resources & State
     * Connect N/A
     * After Connection established: Complete by telling the BB so (onPageConnection)
     */
    connect() {
        if (this._isConnected) {
            console.warn(`Can't connect facade ${this.id}, already connected`);
            return;
        }
        this._isConnected = true;
        this.connected$.next();
        this.buildingBlock.connected$.next();
        this.buildingBlock.onPageConnection();
    }
    disconnect() {
        if (!this._isConnected) {
            console.warn(`Can't disconnect facade ${this.id}, not connected`);
            return;
        }
        this._isConnected = false;
        this.disconnected$.next();
        this.buildingBlock.disconnected$.next();
        this.buildingBlock.onPageDisconnected();
    }
    markFormGroupAsTouched() {
        var _a;
        (_a = this.buildingBlock.getForm()) === null || _a === void 0 ? void 0 : _a.markAllAsTouched();
    }
}

/**
 * A Noop Facade (doing nothing) standing in for any future Facade we create.
 * There will be a Facade to connect to PFE but we are actually not limited.
 */
class NoopFacade extends AbstractBuildingBlockFacade {
    static create(block) {
        return new NoopFacade(block);
    }
    connect() {
        const block = this.buildingBlock;
        block.onPageConnection();
    }
}

var BUILDING_BLOCK_NAVIGATION_TYPE;
(function (BUILDING_BLOCK_NAVIGATION_TYPE) {
    BUILDING_BLOCK_NAVIGATION_TYPE["Page"] = "NAVIGATE_PAGE";
    BUILDING_BLOCK_NAVIGATION_TYPE["Next"] = "NAVIGATE_NEXT";
    BUILDING_BLOCK_NAVIGATION_TYPE["Back"] = "NAVIGATE_BACK";
    BUILDING_BLOCK_NAVIGATION_TYPE["Home"] = "NAVIGATE_HOME";
})(BUILDING_BLOCK_NAVIGATION_TYPE || (BUILDING_BLOCK_NAVIGATION_TYPE = {}));
const isImplicitNavigation = (value) => value === BUILDING_BLOCK_NAVIGATION_TYPE.Back ||
    value === BUILDING_BLOCK_NAVIGATION_TYPE.Next ||
    value === BUILDING_BLOCK_NAVIGATION_TYPE.Home;

var ABSTRACT_PAGE_STATUS;
(function (ABSTRACT_PAGE_STATUS) {
    ABSTRACT_PAGE_STATUS["WAITING"] = "WAITING";
    ABSTRACT_PAGE_STATUS["COMPLETE"] = "COMPLETE";
})(ABSTRACT_PAGE_STATUS || (ABSTRACT_PAGE_STATUS = {}));
/**
 * Create a stream of `WAITING` or `COMPLETE` signals depending on the status
 * of all Building Block currently on the page.
 */
function createPageStatusStream(buildingBlockQueryList) {
    /**
     * Create a stream of Building Blocks instances on our current page
     * Use startWith to always begin with the current value
     */
    const currentBuildingBlock$ = buildingBlockQueryList.changes.pipe(startWith(buildingBlockQueryList), map((value) => value.toArray()));
    // wait for completion of Building Block
    return currentBuildingBlock$.pipe(
    /**
     * Switch the stream for the list of building blocks to their completion signals (completion$)
     * The defaultIfEmpty ensures that this stream always emits as the combineLatest would wait for all members to emit
     * which never happens with an empty stream
     */
    switchMap((items) => {
        const completionEvents = items.map((item) => item.completion$);
        return combineLatest(completionEvents).pipe(defaultIfEmpty([]));
    }), 
    // now check if all Building Blocks are completed. Empty List is considered as completed too
    map((values) => {
        const isComplete = values.every((status) => status === true);
        if (isComplete) {
            return ABSTRACT_PAGE_STATUS.COMPLETE;
        }
        else {
            return ABSTRACT_PAGE_STATUS.WAITING;
        }
    }), 
    // Prevent that we call onPageComplete or onPageWaiting multiple times
    distinctUntilChanged());
}
/**
 * Maintain a stream of added & removed Building Block instances
 * so we can either cleanup removed Building Blocks or add missingFacades
 */
function createBuildBlockChangesStream(queryList) {
    return queryList.changes.pipe(
    /**
     * The Query List isn't a BehaviourSubject so we start with the current state
     * any other change will then create the expected diff of added & removed instances
     */
    startWith(queryList), map((value) => value.toArray()), pairwise(), 
    /**
     * we want to start exactly with this state so we can initialize on subscription
     * otherwise we would have to wait for the first change on the query list
     */
    startWith([[], queryList.toArray()]), map(([previous, current]) => {
        const addedBuildingBlocks = current.filter((x) => !previous.includes(x));
        const removedBuildingBlocks = previous.filter((x) => !current.includes(x));
        return [addedBuildingBlocks, removedBuildingBlocks];
    }));
}

/**
 * This service will be embed into `AbstractBuildingBlockPage`
 * which acts as the base class of all Building Block Pages.
 *
 * Any page being created can report it's set of `TalyPageData`
 * so it can be used in a different place, outside of the actual page component.
 */
class TalyPageDataService {
    constructor() {
        this._pageDataSubject = new BehaviorSubject({});
    }
    storeData(data) {
        this._currentPageData = data;
        this._pageDataSubject.next(this._currentPageData);
    }
    get pageData$() {
        return this._pageDataSubject.asObservable();
    }
    get pageData() {
        return this._currentPageData;
    }
}
TalyPageDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyPageDataService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TalyPageDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyPageDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyPageDataService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * As of now this service is used to signal that a user has requested to
 * navigate to the next page. This service is needed to forward this event
 * from the navigation-service to other core services.
 */
class TalyPageService {
    constructor() {
        this._nextPageRequested$ = new Subject();
        this.nextPageRequested$ = this._nextPageRequested$.asObservable();
    }
    onNextPageRequested() {
        this._nextPageRequested$.next();
    }
}
TalyPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyPageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TalyPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: TalyPageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * The abstract component any Building Block needs to inherit from
 * in order to be discoverable. The page is not designed to connect & disconnect
 * multiple times during the lifetime. We expect exactly one connect and one disconnect
 * over the course of its lifetime.
 *
 * That's why we also protect the methods connect & disconnect as they are not intended to be called from the outside
 * in an unexpected order.
 */
let AbstractBuildingBlockPage = class AbstractBuildingBlockPage {
    constructor(injector) {
        this.pageData = {};
        this.disconnect$ = new Subject();
        this.connect$ = new Subject();
        this.status = ABSTRACT_PAGE_STATUS.WAITING;
        this.facadeMap = new Map();
        this._pageDataService = injector.get(TalyPageDataService);
        this._pageService = injector.get(TalyPageService);
    }
    get facades() {
        return Array.from(this.facadeMap.values());
    }
    onPageReady() { }
    onPageComplete() { }
    onPageWaiting() { }
    onPageDestroy() { }
    ngOnInit() {
        this._pageDataService.storeData(this.pageData);
        this._pageService.nextPageRequested$.pipe(untilDestroyed(this)).subscribe(() => {
            this.onNextPageRequested();
        });
    }
    ngAfterViewInit() {
        /**
         * Wait for the next tick to prevent any expression has changed error
         * this should make developer's life easier while the impact
         * of deferring just everything by one tick us unknown.
         */
        setTimeout(() => {
            this.setupBuildingBlocks();
            this.connect();
            this.onPageReady();
        });
    }
    setupBuildingBlocks() {
        const statusUpdate$ = createPageStatusStream(this._buildingBlockQueryList).pipe(tap((status) => this.handlePageStatusUpdates(status)));
        const buildingBlockUpdate$ = createBuildBlockChangesStream(this._buildingBlockQueryList).pipe(tap((data) => this.handleBuildBlocksChanged(data)));
        this.connect$
            .pipe(switchMap(() => {
            return merge(statusUpdate$, buildingBlockUpdate$);
        }), takeUntil(this.disconnect$))
            .subscribe();
        this.disconnect$
            .pipe(take(1), tap(() => {
            this.facades.forEach((facade) => {
                facade.disconnect();
            });
        }))
            .subscribe();
    }
    /**
     * Whenever the set of Building Blocks has changed
     * ensure that the matching facades are created & connected or respectively disconnected and deleted
     * from our internal facade map. We chose this approach instead of reusing facades
     * as the underlying component is in fact deleted and recreated by the Angular renderer
     * so we would have to introduce extra logic to re-initialize that component. It's
     * easier to pretend that we encounter a Building Block the first time.
     */
    handleBuildBlocksChanged([added, removed], allowedRetries = 2) {
        removed.forEach((item) => {
            const facade = this.facadeMap.get(item.id);
            if (facade) {
                facade.disconnect();
                this.facadeMap.delete(facade.id);
            }
        });
        const retryItems = [];
        added.forEach((item) => {
            const facade = this.createFacade(item);
            if (this.facadeMap.has(facade.id)) {
                if (allowedRetries === 0) {
                    throw new Error(`The Building Block with ID '${facade.id}' collides with a previously encountered Building Block.`);
                }
                retryItems.push(item);
            }
            else {
                this.facadeMap.set(facade.id, facade);
                facade.connect();
            }
        });
        /*
            Why do we need a retry mechanism?
            refer: https://github.developer.allianz.io/ilt/taly-workspace/issues/867
    
            A building block can be either be shown in the content area or in sidebar area. When we resize the browser,
            it can happen that the building block is shown both in the content area and in the sidebar for a tick
            or two (As sidebar directive & sidebar component add/remove the building blocks at the same time and are not in sync with
            each other).
    
            So we need to wait until the existing building block is completely removed and then
            create the facade for it again.
        */
        if (allowedRetries > 0 && retryItems.length > 0) {
            setTimeout(() => this.handleBuildBlocksChanged([retryItems, []], allowedRetries - 1), 50);
        }
    }
    handlePageStatusUpdates(newStatus) {
        this.status = newStatus;
        if (this.status === ABSTRACT_PAGE_STATUS.COMPLETE) {
            this.onPageComplete();
        }
        else {
            this.onPageWaiting();
        }
    }
    connect() {
        this.connect$.next();
    }
    disconnect() {
        this.disconnect$.next();
    }
    onNextPageRequested() {
        this.facades.forEach((facade) => facade.markFormGroupAsTouched());
    }
    get buildingBlocks() {
        return this._buildingBlockQueryList.toArray();
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createFacade(block) {
        throw new Error('Provide a facade implementation');
    }
    ngOnDestroy() {
        this.disconnect();
        this.onPageDestroy();
    }
};
AbstractBuildingBlockPage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AbstractBuildingBlockPage, deps: [{ token: Injector }], target: i0.ɵɵFactoryTarget.Directive });
AbstractBuildingBlockPage.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.9", type: AbstractBuildingBlockPage, viewQueries: [{ propertyName: "_buildingBlockQueryList", predicate: AbstractBuildingBlock, descendants: true }], ngImport: i0 });
AbstractBuildingBlockPage = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [Injector])
], AbstractBuildingBlockPage);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: AbstractBuildingBlockPage, decorators: [{
            type: Directive
        }], ctorParameters: function () {
        return [{ type: i0.Injector, decorators: [{
                        type: Inject,
                        args: [Injector]
                    }] }];
    }, propDecorators: { _buildingBlockQueryList: [{
                type: ViewChildren,
                args: [AbstractBuildingBlock]
            }] } });

/**
 * A component that helps visualizing how a component acts as a Building Block.
 * Output ID, Features & Complete Status. Access State.
 */
// eslint-disable-next-line @angular-eslint/component-class-suffix
class BuildingBlockDebugger {
    constructor(detectedBuildingBlock) {
        this.debuggerOpened = false;
        this.exampleState = {};
        this.exampleResources = {};
        this.buildingBlock = detectedBuildingBlock;
    }
    set buildingBlock(value) {
        this._buildingBlock = value;
    }
    get buildingBlock() {
        return this._buildingBlock;
    }
    get buildingBlockId() {
        return this.buildingBlock.id ? this.buildingBlock.id : 'N/A';
    }
    get completion$() {
        return this.buildingBlock.completion$;
    }
    setExampleState(evt) {
        console.log(`Forward Example State to Building Block '${this.buildingBlock.id}' with value`, this.exampleState);
        this.buildingBlock.setState(this.exampleState);
        if (evt) {
            evt.stopPropagation();
        }
    }
    setExampleResources() {
        this.buildingBlock.setResources(this.exampleResources);
    }
    revertCompletion() {
        this.buildingBlock.revertCompletion();
    }
    commitCompletion() {
        this.buildingBlock.commitCompletion();
    }
    clear() {
        this.buildingBlock.setState({});
        this.buildingBlock.setResources({});
    }
    logState() {
        console.log(this.buildingBlock.getState());
    }
    ngOnInit() {
        if (!this.buildingBlock) {
            throw new Error('You used the Building Block Debugger without providing a Building Block ([buildingBlock]) or using it insides a given Building Block');
        }
    }
}
BuildingBlockDebugger.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockDebugger, deps: [{ token: AbstractBuildingBlock, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Component });
BuildingBlockDebugger.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.9", type: BuildingBlockDebugger, selector: "building-block-debugger", inputs: { exampleState: "exampleState", exampleResources: "exampleResources", buildingBlock: "buildingBlock" }, ngImport: i0, template: "<div class=\"debug-opener {{ debuggerOpened ? 'debug-opener--opened' : '' }}\" (click)=\"debuggerOpened = !debuggerOpened\">\n  <svg class=\"debug-opener--svg\" *ngIf=\"(completion$ | async) === false\" name=\"waiting\" width=\"30\" height=\"30\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M6.75 12C6.75 9.10051 9.10051 6.75 12 6.75C14.8995 6.75 17.25 9.10051 17.25 12C17.25 14.8995 14.8995 17.25 12 17.25C9.10051 17.25 6.75 14.8995 6.75 12ZM12 5.25C8.27208 5.25 5.25 8.27208 5.25 12C5.25 15.7279 8.27208 18.75 12 18.75C15.7279 18.75 18.75 15.7279 18.75 12C18.75 8.27208 15.7279 5.25 12 5.25ZM11.25 8V12C11.25 12.1989 11.329 12.3897 11.4697 12.5303L13.4697 14.5303L14.5303 13.4697L12.75 11.6893V8H11.25Z\" fill=\"#C4C4C4\"/>\n  </svg>\n  <svg class=\"debug-opener--svg\" *ngIf=\"(completion$ | async)\" name=\"check\" width=\"30\" height=\"30\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <circle cx=\"12\" cy=\"12\" r=\"8\" fill=\"#75F0C7\"/>\n    <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M16.6002 9.44999L12.1002 15.45C11.9768 15.6145 11.7914 15.7211 11.5872 15.7449C11.383 15.7688 11.1779 15.7078 11.02 15.5762L8.02002 13.0762L8.9803 11.9238L11.3738 13.9184L15.4002 8.54999L16.6002 9.44999Z\" fill=\"#202840\"/>\n  </svg>\n  {{buildingBlockId}}\n  <a *ngIf=\"exampleState\" class=\"debug-opener-button\" (click)=\"setExampleState($event)\" title=\"Set example state data\">Set State</a>\n  <span class=\"triangle\"><span class=\"{{ debuggerOpened ? 'triangle-up' : 'triangle-down' }}\"></span></span>\n</div>\n\n<div class=\"debug-content\" *ngIf=\"debuggerOpened\">\n  <div>Actions</div>\n  <div class=\"debug-actions\">\n    <a class=\"debug-pill-btn\" *ngIf=\"(completion$ | async) === false\" (click)=\"commitCompletion()\">Complete</a>\n    <a class=\"debug-pill-btn\" *ngIf=\"(completion$ | async)\" (click)=\"revertCompletion()\">Reset Completion</a>\n    <a class=\"debug-pill-btn\" (click)=\"clear()\">Clear State</a>\n    <a class=\"debug-pill-btn\" (click)=\"logState()\">Log State</a>\n    <a class=\"debug-pill-btn\" *ngIf=\"exampleResources\" (click)=\"setExampleResources()\">Set Resources (Example)</a>\n    <a class=\"debug-pill-btn\" *ngIf=\"exampleState\" (click)=\"setExampleState()\">Set State (Example)</a>\n  </div>\n  <div class=\"debug-status\">\n      <div class=\"text-waiting\" *ngIf=\"(completion$ | async) === false\">\n        Waiting &nbsp; <svg name=\"waiting\" class=\"waiting\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n          <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M6.75 12C6.75 9.10051 9.10051 6.75 12 6.75C14.8995 6.75 17.25 9.10051 17.25 12C17.25 14.8995 14.8995 17.25 12 17.25C9.10051 17.25 6.75 14.8995 6.75 12ZM12 5.25C8.27208 5.25 5.25 8.27208 5.25 12C5.25 15.7279 8.27208 18.75 12 18.75C15.7279 18.75 18.75 15.7279 18.75 12C18.75 8.27208 15.7279 5.25 12 5.25ZM11.25 8V12C11.25 12.1989 11.329 12.3897 11.4697 12.5303L13.4697 14.5303L14.5303 13.4697L12.75 11.6893V8H11.25Z\" fill=\"#C4C4C4\"/>\n        </svg>\n      </div>\n      <div class=\"text-success\" *ngIf=\"(completion$ | async)\">\n        Complete <svg name=\"complete\" class=\"complete\" width=\"30\" height=\"30\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n          <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M16.1002 9.44999L11.6002 15.45C11.4768 15.6145 11.2914 15.7211 11.0872 15.7449C10.883 15.7688 10.6779 15.7078 10.52 15.5762L7.52002 13.0762L8.4803 11.9238L10.8738 13.9184L14.9002 8.54999L16.1002 9.44999Z\" fill=\"#75F0C7\"/>\n        </svg>\n      </div>\n  </div>\n  <!--\n    Allow to output additional content that might be given\n    through ng-content (like additional building-block-debugger interactions with the BB).\n    That content would be included in the\n    open/closing panel and provides a nice building-block-debugger experience on top\n  -->\n  <ng-content></ng-content>\n</div>\n", styles: [".bg-success{background-color:#75f0c7}.bg-waiting{background-color:#c4c4c4}.text-success{color:#75f0c7}.text-waiting{color:#c4c4c4}.debug-opener{cursor:pointer;background-color:#202840;color:#fff;padding:8px 12px;line-height:28px;font-size:16px;font-weight:700;border-radius:8px;height:44px}.debug-opener--opened{border-bottom-left-radius:0;border-bottom-right-radius:0}.triangle{position:relative;display:inline-block;width:20px;height:16px}.triangle-down,.triangle-up{position:absolute;top:50%;left:40%;width:0;height:0;border-style:solid}.triangle-down{border-width:7px 5px 0 5px;border-color:#fff transparent transparent transparent}.triangle-up{border-width:0 5px 7px 5px;border-color:transparent transparent #fff transparent}.debug-content{color:#fff;background:#363d53;padding:12px 8px 12px 12px;border-bottom-left-radius:8px;border-bottom-right-radius:8px}:host{display:inline-block;padding-bottom:10px}.debug-opener-button{font-size:15px;line-height:24px;padding:1px 8px;margin:0 10px;background:#668cff;border:1px solid #668cff;color:#fff;border-radius:3px;display:inline-block;transition:border-color .3s ease}.debug-opener-button:hover,.debug-opener-button:focus{border-color:#ccc}.debug-opener--svg{margin-bottom:-11px}.debug-actions{margin:8px 0}.debug-pill-btn{background-color:#202840;border-radius:3px;padding:5px 7px;margin-right:5px;font-weight:700;border:1px solid #202840;cursor:pointer;transition:border-color .3s ease}.debug-pill-btn:last-child{margin-right:0}.debug-pill-btn:hover,.debug-pill-btn:focus{border-color:#ccc}.debug-status{margin-top:16px;font-weight:700}.debug-status>*{display:inline-block}.debug-status .waiting{margin-bottom:-8px}.debug-status .complete{margin-bottom:-10px}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i2.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockDebugger, decorators: [{
            type: Component,
            args: [{ selector: 'building-block-debugger', template: "<div class=\"debug-opener {{ debuggerOpened ? 'debug-opener--opened' : '' }}\" (click)=\"debuggerOpened = !debuggerOpened\">\n  <svg class=\"debug-opener--svg\" *ngIf=\"(completion$ | async) === false\" name=\"waiting\" width=\"30\" height=\"30\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M6.75 12C6.75 9.10051 9.10051 6.75 12 6.75C14.8995 6.75 17.25 9.10051 17.25 12C17.25 14.8995 14.8995 17.25 12 17.25C9.10051 17.25 6.75 14.8995 6.75 12ZM12 5.25C8.27208 5.25 5.25 8.27208 5.25 12C5.25 15.7279 8.27208 18.75 12 18.75C15.7279 18.75 18.75 15.7279 18.75 12C18.75 8.27208 15.7279 5.25 12 5.25ZM11.25 8V12C11.25 12.1989 11.329 12.3897 11.4697 12.5303L13.4697 14.5303L14.5303 13.4697L12.75 11.6893V8H11.25Z\" fill=\"#C4C4C4\"/>\n  </svg>\n  <svg class=\"debug-opener--svg\" *ngIf=\"(completion$ | async)\" name=\"check\" width=\"30\" height=\"30\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <circle cx=\"12\" cy=\"12\" r=\"8\" fill=\"#75F0C7\"/>\n    <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M16.6002 9.44999L12.1002 15.45C11.9768 15.6145 11.7914 15.7211 11.5872 15.7449C11.383 15.7688 11.1779 15.7078 11.02 15.5762L8.02002 13.0762L8.9803 11.9238L11.3738 13.9184L15.4002 8.54999L16.6002 9.44999Z\" fill=\"#202840\"/>\n  </svg>\n  {{buildingBlockId}}\n  <a *ngIf=\"exampleState\" class=\"debug-opener-button\" (click)=\"setExampleState($event)\" title=\"Set example state data\">Set State</a>\n  <span class=\"triangle\"><span class=\"{{ debuggerOpened ? 'triangle-up' : 'triangle-down' }}\"></span></span>\n</div>\n\n<div class=\"debug-content\" *ngIf=\"debuggerOpened\">\n  <div>Actions</div>\n  <div class=\"debug-actions\">\n    <a class=\"debug-pill-btn\" *ngIf=\"(completion$ | async) === false\" (click)=\"commitCompletion()\">Complete</a>\n    <a class=\"debug-pill-btn\" *ngIf=\"(completion$ | async)\" (click)=\"revertCompletion()\">Reset Completion</a>\n    <a class=\"debug-pill-btn\" (click)=\"clear()\">Clear State</a>\n    <a class=\"debug-pill-btn\" (click)=\"logState()\">Log State</a>\n    <a class=\"debug-pill-btn\" *ngIf=\"exampleResources\" (click)=\"setExampleResources()\">Set Resources (Example)</a>\n    <a class=\"debug-pill-btn\" *ngIf=\"exampleState\" (click)=\"setExampleState()\">Set State (Example)</a>\n  </div>\n  <div class=\"debug-status\">\n      <div class=\"text-waiting\" *ngIf=\"(completion$ | async) === false\">\n        Waiting &nbsp; <svg name=\"waiting\" class=\"waiting\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n          <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M6.75 12C6.75 9.10051 9.10051 6.75 12 6.75C14.8995 6.75 17.25 9.10051 17.25 12C17.25 14.8995 14.8995 17.25 12 17.25C9.10051 17.25 6.75 14.8995 6.75 12ZM12 5.25C8.27208 5.25 5.25 8.27208 5.25 12C5.25 15.7279 8.27208 18.75 12 18.75C15.7279 18.75 18.75 15.7279 18.75 12C18.75 8.27208 15.7279 5.25 12 5.25ZM11.25 8V12C11.25 12.1989 11.329 12.3897 11.4697 12.5303L13.4697 14.5303L14.5303 13.4697L12.75 11.6893V8H11.25Z\" fill=\"#C4C4C4\"/>\n        </svg>\n      </div>\n      <div class=\"text-success\" *ngIf=\"(completion$ | async)\">\n        Complete <svg name=\"complete\" class=\"complete\" width=\"30\" height=\"30\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n          <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M16.1002 9.44999L11.6002 15.45C11.4768 15.6145 11.2914 15.7211 11.0872 15.7449C10.883 15.7688 10.6779 15.7078 10.52 15.5762L7.52002 13.0762L8.4803 11.9238L10.8738 13.9184L14.9002 8.54999L16.1002 9.44999Z\" fill=\"#75F0C7\"/>\n        </svg>\n      </div>\n  </div>\n  <!--\n    Allow to output additional content that might be given\n    through ng-content (like additional building-block-debugger interactions with the BB).\n    That content would be included in the\n    open/closing panel and provides a nice building-block-debugger experience on top\n  -->\n  <ng-content></ng-content>\n</div>\n", styles: [".bg-success{background-color:#75f0c7}.bg-waiting{background-color:#c4c4c4}.text-success{color:#75f0c7}.text-waiting{color:#c4c4c4}.debug-opener{cursor:pointer;background-color:#202840;color:#fff;padding:8px 12px;line-height:28px;font-size:16px;font-weight:700;border-radius:8px;height:44px}.debug-opener--opened{border-bottom-left-radius:0;border-bottom-right-radius:0}.triangle{position:relative;display:inline-block;width:20px;height:16px}.triangle-down,.triangle-up{position:absolute;top:50%;left:40%;width:0;height:0;border-style:solid}.triangle-down{border-width:7px 5px 0 5px;border-color:#fff transparent transparent transparent}.triangle-up{border-width:0 5px 7px 5px;border-color:transparent transparent #fff transparent}.debug-content{color:#fff;background:#363d53;padding:12px 8px 12px 12px;border-bottom-left-radius:8px;border-bottom-right-radius:8px}:host{display:inline-block;padding-bottom:10px}.debug-opener-button{font-size:15px;line-height:24px;padding:1px 8px;margin:0 10px;background:#668cff;border:1px solid #668cff;color:#fff;border-radius:3px;display:inline-block;transition:border-color .3s ease}.debug-opener-button:hover,.debug-opener-button:focus{border-color:#ccc}.debug-opener--svg{margin-bottom:-11px}.debug-actions{margin:8px 0}.debug-pill-btn{background-color:#202840;border-radius:3px;padding:5px 7px;margin-right:5px;font-weight:700;border:1px solid #202840;cursor:pointer;transition:border-color .3s ease}.debug-pill-btn:last-child{margin-right:0}.debug-pill-btn:hover,.debug-pill-btn:focus{border-color:#ccc}.debug-status{margin-top:16px;font-weight:700}.debug-status>*{display:inline-block}.debug-status .waiting{margin-bottom:-8px}.debug-status .complete{margin-bottom:-10px}\n"] }]
        }], ctorParameters: function () {
        return [{ type: AbstractBuildingBlock, decorators: [{
                        type: Optional
                    }, {
                        type: SkipSelf
                    }] }];
    }, propDecorators: { exampleState: [{
                type: Input
            }], exampleResources: [{
                type: Input
            }], buildingBlock: [{
                type: Input
            }] } });

class BuildingBlockDebuggerModule {
}
BuildingBlockDebuggerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockDebuggerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
BuildingBlockDebuggerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockDebuggerModule, declarations: [BuildingBlockDebugger], imports: [CommonModule], exports: [BuildingBlockDebugger] });
BuildingBlockDebuggerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockDebuggerModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BuildingBlockDebuggerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [BuildingBlockDebugger],
                    exports: [BuildingBlockDebugger]
                }]
        }] });

const dateUnitAlias = {
    d: 'd',
    day: 'd',
    days: 'd',
    w: 'w',
    week: 'w',
    weeks: 'w',
    m: 'm',
    month: 'm',
    months: 'm',
    y: 'y',
    year: 'y',
    years: 'y'
};
function constructNaturalDateRegexp() {
    const acceptedUnits = Object.keys(dateUnitAlias);
    return new RegExp(`^today(?:(\\+|-)(\\d+)(${acceptedUnits.join('|')}))?$`, 'i');
}
// This guard can be replaced by a regex type as soon as TypeScript supports them.
// There is currently at least one request for these types,
// see https://github.com/microsoft/TypeScript/issues/41160
function isNaturalDateString(maybeDateString) {
    return constructNaturalDateRegexp().test(maybeDateString);
}
function parseNaturalDateString(maybeDateString) {
    if (!isNaturalDateString(maybeDateString)) {
        return null;
    }
    const dateSegments = maybeDateString.match(constructNaturalDateRegexp());
    let date = dayjs();
    if (dateSegments) {
        let amount = parseInt(dateSegments[2], 10);
        amount = dateSegments[1] === '-' ? amount * -1 : amount;
        const unit = (dateUnitAlias[dateSegments[3]] ||
            dateSegments[3]);
        date = updateDate(date, amount, unit);
    }
    return date.format('YYYY-MM-DD');
}
function updateDate(date, amount, unit) {
    if (!amount || !unit)
        return date;
    return dayjs(date).add(amount, unit === 'm' ? 'M' : unit);
}

function createDateStringFormatError(item, key) {
    return new Error(`The value you provided as the ${key} for the ${item.type} validator could not be parsed as a valid date!

Your input: "${item[key]}"

Allowed inputs:
- the RFC 3339 date format ("yyyy-mm-dd", e.g. "2002-07-01")
- our own natural date string format (e.g. "today+2days")`);
}
function isIsoDateString(dateString) {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}
const TRANSLATIONS = {
    required: $localize `:@@validation.error.required:This field is required!`,
    requiredTrue: $localize `:@@validation.error.requiredtrue:This field is required to be true!`,
    min: (min) => $localize `:@@validaton.error.min: This fields needs to have a minimal value of ${min}:min:.`,
    max: (max) => $localize `:@@validaton.error.max: This fields needs to have a maximum value of ${max}:max:.`,
    minLength: (minLength) => $localize `:@@validation.error.minlength:This field can only have a min length of ${minLength}:minlength: characters.`,
    maxLength: (maxLength) => $localize `:@@validation.error.maxlength:This field can only have a max length of ${maxLength}:maxlength: characters.`,
    minDate: $localize `:@@validation.error.mindate:This field requires a more recent date`,
    maxDate: $localize `:@@validation.error.maxdate:This field requires a older date`,
    email: $localize `:@@validation.error.email:This field requires a valid email`,
    pattern: $localize `:@@validation.error.pattern:This field has invalid format.`
};
const BASE_VALIDATIONS = {
    REQUIRED: (item) => {
        return {
            validator: Validators.required,
            validatorName: 'required',
            errorMessage: item.errorMessage || TRANSLATIONS.required
        };
    },
    REQUIRED_TRUE: (item) => {
        return {
            validator: Validators.requiredTrue,
            validatorName: 'required',
            errorMessage: item.errorMessage || TRANSLATIONS.requiredTrue
        };
    },
    MIN: (item) => {
        const minValue = item.min;
        return {
            min: minValue,
            validator: Validators.min(minValue),
            validatorName: 'min',
            errorMessage: item.errorMessage || TRANSLATIONS.min(minValue)
        };
    },
    MAX: (item) => {
        const maxValue = item.max;
        return {
            max: maxValue,
            validator: Validators.max(maxValue),
            validatorName: 'max',
            errorMessage: item.errorMessage || TRANSLATIONS.max(maxValue)
        };
    },
    MIN_LENGTH: (item) => {
        const minLength = item.minLength;
        return {
            minLength,
            validator: Validators.minLength(minLength),
            validatorName: 'minlength',
            errorMessage: item.errorMessage || TRANSLATIONS.minLength(minLength)
        };
    },
    MAX_LENGTH: (item) => {
        const maxLength = item.maxLength;
        return {
            maxLength,
            validator: Validators.maxLength(maxLength),
            validatorName: 'maxlength',
            errorMessage: item.errorMessage || TRANSLATIONS.maxLength(maxLength)
        };
    },
    MIN_DATE: (item) => {
        const itemMinDate = item.minDate;
        let minDate;
        if (isNaturalDateString(itemMinDate)) {
            minDate = parseNaturalDateString(itemMinDate);
        }
        else if (isIsoDateString(itemMinDate)) {
            minDate = itemMinDate;
        }
        else {
            throw createDateStringFormatError(item, 'minDate');
        }
        return {
            minDate,
            validator({ value }) {
                if (!value) {
                    return null;
                }
                const date = dayjs(value);
                if (date.isBefore(minDate)) {
                    return {
                        mindate: {
                            actual: date.format('YYYY-MM-DD'),
                            minDate: minDate
                        }
                    };
                }
                return null;
            },
            validatorName: 'mindate',
            errorMessage: item.errorMessage || TRANSLATIONS.minDate
        };
    },
    MAX_DATE: (item) => {
        const itemMaxDate = item.maxDate;
        let maxDate;
        if (isNaturalDateString(itemMaxDate)) {
            maxDate = parseNaturalDateString(itemMaxDate);
        }
        else if (isIsoDateString(itemMaxDate)) {
            maxDate = itemMaxDate;
        }
        else {
            throw createDateStringFormatError(item, 'maxDate');
        }
        return {
            maxDate,
            validator({ value }) {
                if (!value) {
                    return null;
                }
                const date = dayjs(value);
                if (date.isAfter(maxDate)) {
                    return {
                        maxdate: {
                            actual: date.format('YYYY-MM-DD'),
                            maxDate: maxDate
                        }
                    };
                }
                return null;
            },
            validatorName: 'maxdate',
            errorMessage: item.errorMessage || TRANSLATIONS.maxDate
        };
    },
    EMAIL: (item) => {
        const topLevelDomainValidator = (control) => {
            if (!control.value || !control.value.includes('@')) {
                return null;
            }
            const emailParts = control.value.split('@');
            const domainPart = emailParts[emailParts.length - 1];
            if (domainPart.includes('.')) {
                return null;
            }
            return { email: true };
        };
        // Validators.compose() possibly returns null, but only if we provide an
        // empty array. In this case we definitely know that the array is not
        // empty so we can safely disable the no-non-null-assertion rule
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const resultingValidator = Validators.compose([Validators.email, topLevelDomainValidator]);
        return {
            validator: resultingValidator,
            validatorName: 'email',
            errorMessage: item.errorMessage || TRANSLATIONS.email
        };
    },
    PATTERN: (item) => {
        return {
            pattern: item.pattern,
            validator: Validators.pattern(item.pattern),
            validatorName: 'pattern',
            errorMessage: item.errorMessage || TRANSLATIONS.pattern
        };
    }
};

function isFormGroup(control) {
    // duck test if this is a FormGroup
    return (control &&
        Object.prototype.hasOwnProperty.call(control, 'controls') &&
        control instanceof FormGroup);
}
function isFormArray(control) {
    // duck test if this is a FormArray
    return (control &&
        Object.prototype.hasOwnProperty.call(control, 'controls') &&
        Array.isArray(control.controls));
}
function isFormControl(control) {
    return control && control instanceof FormControl;
}

/**
 * @deprecated This was never meant for public usage and will be removed in one of the next major versions.
 * If you really need to dynamically add validators to a control, please use the Angular built-in tools to
 * achieve this (i.e. `Validators.compose(...)`, `control.setValidators(...)`).
 */
function applyValidationToControl(control, validators = [], asyncValidators = []) {
    /**
     * Compose the new rules with any existing set of validations
     * otherwise they are lost. We had that with ndbx's nxDatefield validations
     * which are lost the moment we apply the validation config.
     */
    const combined = Validators.compose([...validators, control.validator]);
    control.setValidators(combined);
    const combinedAsync = Validators.composeAsync([...asyncValidators, control.asyncValidator]);
    control.setAsyncValidators(combinedAsync);
    control.updateValueAndValidity({ emitEvent: false });
}
/**
 * Apply a validation configuration to a form group.
 * For each accepted validation (as we only support a subset of validations) we will create a
 * configuration entry (ValidationConfig) which is used to find the control & apply the validations.
 *
 * Validation Error Message are independent of the form control. The form control only knows about the validation name
 * that goes wrong. The actual processing of those needs to happen in the template. For this purpose we return the entire
 * map of ValidationConfig objects to easily access the errorMessages available in there.
 */
// TODO: make third parameter "componentInstance" mandatory
function applyValidationConfig(form, config, componentInstance) {
    const validations = Object.assign(Object.assign({}, BASE_VALIDATIONS), getPluginValidations());
    const validationMap = parseValidationConfiguration(config, validations);
    for (const [key] of validationMap) {
        const keyParts = key.split('.');
        const [validators, asyncValidators] = getValidatorsForControlPath(keyParts, validationMap);
        const isFormArrayKey = key.includes('.*.');
        if (isFormArrayKey) {
            // validation for form array
            const wildcardIndex = key.indexOf('.*.');
            // formArrayKey is the part before the wildcard
            const formArrayKey = key.substring(0, wildcardIndex);
            // controlKey is the part after the wildcard
            const controlKey = key.substring(wildcardIndex + 3);
            const formArray = form.get(formArrayKey);
            if (formArray !== null) {
                for (const arrayControl of formArray.controls) {
                    const control = arrayControl.get(controlKey);
                    safeApplyValidationToControl(key, control, validators, asyncValidators);
                }
            }
        }
        else {
            const control = form.get(key);
            safeApplyValidationToControl(key, control, validators, asyncValidators);
        }
    }
    // listen for value changes so we can detect dynamically added new form controls
    form.valueChanges
        .pipe(componentInstance ? untilDestroyed(componentInstance) : identity, tap(() => {
        const validatorsAdded = addValidatorsToNewControls(form, [], validationMap);
        if (validatorsAdded) {
            form.updateValueAndValidity();
        }
    }))
        .subscribe();
    form.updateValueAndValidity();
    return validationMap;
}
/**
 *
 * Apply validators from the given validationMap to the given control if it's a "new control"
 * (i.e. if it has not had validators applied). If the given control is a form array or form
 * group it will recursively call itself for all child controls.
 *
 * The `controlPath` parameter is used to manage the recursion and is expected to be empty
 * in the initial call.
 *
 * @param control the control to add validators to (can be a form group, form array or form control)
 * @param controlPath the form control path pieces of the given control (e.g. `['group', 'id']`).
 * Used for recursion. Pass in empty array if you are calling this manually.
 * @param validationMap a map of control paths and their associated validators
 * @returns whether a new control has been taken care of
 */
function addValidatorsToNewControls(control, controlPath, validationMap) {
    if (isFormControl(control)) {
        if (!control.talyValidatorsApplied) {
            const [validators, asyncValidators] = getValidatorsForControlPath(controlPath, validationMap);
            safeApplyValidationToControl(controlPath.join('.'), control, validators, asyncValidators);
            return true;
        }
    }
    else if (isFormArray(control)) {
        let validatorsAdded = false;
        for (const [index, arrayControl] of control.controls.entries()) {
            let itemPath = [...controlPath, '*'];
            if (isFormControl(arrayControl)) {
                itemPath = [...controlPath, String(index)];
            }
            validatorsAdded =
                addValidatorsToNewControls(arrayControl, itemPath, validationMap) || validatorsAdded;
        }
        return validatorsAdded;
    }
    else if (isFormGroup(control)) {
        let validatorsAdded = false;
        for (const childControlKey in control.controls) {
            if (Object.prototype.hasOwnProperty.call(control.controls, childControlKey)) {
                const childPath = [...controlPath, childControlKey];
                validatorsAdded =
                    addValidatorsToNewControls(control.controls[childControlKey], childPath, validationMap) ||
                        validatorsAdded;
            }
        }
        return validatorsAdded;
    }
    return false;
}
/**
 * Returns the sync and async validators for a form control path.
 * Will return empty arrays if the given control path has no validators configured.
 *
 * @param controlPath path pieces to a control, e.g. ['group', 'id'] or ['items', '*', 'id']
 * @param validationMap map of control paths and their validation configs
 * @returns tuple with array of sync validators and array of async validators
 */
function getValidatorsForControlPath(controlPath, validationMap) {
    let validators = [];
    let asyncValidators = [];
    if (validationMap.has(controlPath.join('.'))) {
        const validatorsConfig = validationMap.get(controlPath.join('.')) || [];
        validators = validatorsConfig
            .filter((validatorConfig) => !validatorConfig.async)
            .map((item) => item.validator);
        asyncValidators = validatorsConfig
            .filter((validatorConfig) => Boolean(validatorConfig.async))
            .map((item) => item.validator);
    }
    return [validators, asyncValidators];
}
function safeApplyValidationToControl(key, control, validators, asyncValidators) {
    if (control === null) {
        // If the input is not a form-control, log the warning and skip the validation
        console.warn(`The validation for the given key '${key}' could not be performed as it is not matching any form control.
Kindly check if this form control is hidden with ACL policy in (policy.txt)!
Fields those are hidden with ACL cannot be validated and will be ignored.`);
        return;
    }
    applyValidationToControl(control, validators, asyncValidators);
    // We are using this custom flag so we are later able to figure out if a control
    // already received validators. This way we can spot newly added controls
    // and safely apply validators to them.
    control.talyValidatorsApplied = true;
}
/**
 * Very simplistic factory that receives a configuration
 * directly from the pages.json  (e.g. { "required": true})
 * and returns the matching validators from Angular or from the configured plugins.
 *
 * This will be the single place where we can handle validations beyond required
 * including params etc (like min/max).
 *
 * Given a Configuration Item with a type return the matching Validator.
 * This will include the error message from the given configuration or takes a default
 * value which will be part of the application translation.
 */
function validationFactory(item, validations) {
    const validation = validations[item.type];
    return validation ? validation(item) : null;
}
/**
 * Ensure that we don't have duplicates in the given list of validations.
 */
function collectConfigurationItems(config = []) {
    const validatorMap = config.reduce((accu, data) => {
        const containsValidationTypeForId = !!accu.find((item) => item.type === data.type && item.id === data.id);
        if (false === containsValidationTypeForId) {
            accu.push(data);
        }
        return accu;
    }, new Array());
    return validatorMap;
}
/**
 * Pass in the config from a pages.json to retrieve
 * a populated Map of applicable ValidationConfig[] per control id.
 */
function parseValidationConfiguration(config, validations) {
    const configItems = collectConfigurationItems(config);
    const validatorMap = configItems.reduce((accu, itemData) => {
        var _a;
        if (!accu.has(itemData.id)) {
            accu.set(itemData.id, []);
        }
        const validation = validationFactory(itemData, validations);
        validation
            ? (_a = accu.get(itemData.id)) === null || _a === void 0 ? void 0 : _a.push(validation)
            : console.warn(`No validation available for configured validator with type ${itemData.type}.
        Therefore skipping this validator for validation of control ${itemData.id}.
        Kindly check your configuration for validators.`);
        return accu;
    }, new Map());
    return validatorMap;
}

class EventServiceInterface {
}

const EVENT_ADAPTER_TOKEN = new InjectionToken('Token To  Provide Event Adapter');

const ROOT_EVENTS = new InjectionToken('Angular Services For Event Handling');
const _ROOT_EVENTS = new InjectionToken('Angular Services For Event Handling - Internal');
class EventsRootModule {
    // We need to inject the ROOT_EVENTS here. Otherwise none of the event services would be instantiated and registered with PFE Action Service
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    constructor(_rootEvents) { }
}
EventsRootModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsRootModule, deps: [{ token: ROOT_EVENTS }], target: i0.ɵɵFactoryTarget.NgModule });
EventsRootModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsRootModule });
EventsRootModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsRootModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsRootModule, decorators: [{
            type: NgModule,
            args: [{}]
        }], ctorParameters: function () {
        return [{ type: i0.InjectionToken, decorators: [{
                        type: Inject,
                        args: [ROOT_EVENTS]
                    }] }];
    } });
class EventsModule {
    static forRoot(actions) {
        return {
            ngModule: EventsRootModule,
            providers: [
                {
                    provide: _ROOT_EVENTS,
                    useValue: actions
                },
                {
                    provide: ROOT_EVENTS,
                    useFactory: registerEvents,
                    multi: true,
                    deps: [Injector, _ROOT_EVENTS]
                }
            ]
        };
    }
}
EventsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
EventsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsModule });
EventsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: EventsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: []
                }]
        }] });
function registerEvents(injector, events) {
    const adapter = injector.get(EVENT_ADAPTER_TOKEN);
    /**
     * Register all the events to PFE using the Events Adapter.
     * At the moment we have only PFE action use case. So We can provide our own Even Adapter in the future if required.
     */
    const eventServiceInstances = Object.keys(events).map((key) => {
        const value = events[key];
        const service = injector.get(value);
        adapter.registerEvent(key, service.run);
        return service;
    });
    return eventServiceInstances;
}

/**
 * Helper service to integrate API calls with external systems like PFE.
 *
 * Best used with an actual implementation like the `PfeApiCommunicationService`.
 * Provide it in your app root module.
 *
 * {
 *   provide: TalyApiCommunicationService,
 *   useClass: PfeApiCommunicationService
 * }
 */
class TalyApiCommunicationService {
}

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractBuildingBlock, AbstractBuildingBlockFacade, AbstractBuildingBlockPage, BFF_BASE_URL_TOKEN, BUILDING_BLOCK_META_DATA, BUILDING_BLOCK_NAVIGATION_TYPE, BuildingBlockDebugger, BuildingBlockDebuggerModule, BuildingBlockMetaService, CHANNEL_TOKEN, EVENT_ADAPTER_TOKEN, EXPERIMENTAL_NEXT_BUTTON_ALWAYS_ENABLED, EventServiceInterface, EventsModule, EventsRootModule, NoopFacade, PAGES_CONFIGURATION, PLUGIN_VALIDATORS, ROOT_EVENTS, TalyApiCommunicationService, TalyCoreModule, TalyPageDataService, TalyPageService, applyValidationConfig, applyValidationToControl, createBuildingBlockProvider, isImplicitNavigation, isIsoDateString, isNaturalDateString, parseNaturalDateString, registerEvents };
